import React, {useCallback, useEffect, useState} from "react";
import styles, {C} from "../styles";
import {useConfirm, useToast} from "./FeedbackProvider";
import CampaignFormModal from "./CampaignFormModal";
import {
    apiAdminListCampaigns, apiAdminUpdateCampaign, apiAdminDeleteCampaign,
    apiAdminCampaignPreviewLink, apiAdminListCampaignAudit,
} from "../api/adminCampaigns";

const STATE_LABELS = {scheduled: "Programmée", live: "En ligne", ended: "Terminée", disabled: "Désactivée"};
const STATE_COLORS = {
    scheduled: {backgroundColor: C.accentLight, color: C.accentDark},
    live: {backgroundColor: C.primaryLight, color: C.primaryDark},
    ended: {backgroundColor: "#EEE", color: C.textMuted},
    disabled: {backgroundColor: C.dangerLight, color: C.danger},
};
const ACTION_LABELS = {
    create: "Création", replace_file: "Remplacement du fichier", update: "Modification", activate: "Activation",
    deactivate: "Désactivation", delete: "Suppression", preview: "Aperçu",
    isolation_off: "Isolation retirée", isolation_on: "Isolation rétablie",
};

const smallButton = {border: `1px solid ${C.border}`, backgroundColor: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 13};

function formatParis(iso) {
    return new Date(iso).toLocaleString("fr-FR", {timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"})
        .replace(",", "");
}

export default function AdminCampaigns() {
    const confirm = useConfirm();
    const {notify} = useToast();
    const [campaigns, setCampaigns] = useState([]);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(undefined); // undefined = fermé, null = création, objet = modification
    const [audit, setAudit] = useState(null);

    const load = useCallback(async () => {
        const data = await apiAdminListCampaigns();
        if (!Array.isArray(data)) {
            setError(data?.message || data?.error || "Impossible de charger les campagnes.");
            setCampaigns([]);
            return;
        }
        setError("");
        setCampaigns(data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const publicUrl = campaign => `${window.location.origin}${campaign.url}`;

    async function copyLink(campaign) {
        try {
            await navigator.clipboard.writeText(publicUrl(campaign));
            notify("Lien copié.");
        } catch {
            notify(`Copie impossible : ${publicUrl(campaign)}`, "error");
        }
    }

    async function openPreview(campaign) {
        const result = await apiAdminCampaignPreviewLink(campaign.id);
        if (result?.error) { notify(result.message || result.error, "error"); return; }
        const url = `${window.location.origin}${result.url}`;
        // Si le navigateur bloque l'ouverture (fenêtre surgissante), on donne le lien : il vaut 10 minutes.
        if (!window.open(url, "_blank")) notify(`Aperçu (valable 10 minutes) : ${url}`);
    }

    async function toggleActive(campaign) {
        const verb = campaign.active ? "Désactiver" : "Activer";
        const consequence = campaign.active ? "La page répondra 404 immédiatement." : "La page sera de nouveau servie selon ses dates.";
        if (!(await confirm(`${verb} la campagne « ${campaign.name} » ? ${consequence}`))) return;
        const result = await apiAdminUpdateCampaign(campaign.id, {active: !campaign.active});
        if (result?.error) { notify(result.message || result.error, "error"); return; }
        load();
    }

    async function remove(campaign) {
        if (!(await confirm(`Supprimer définitivement la campagne « ${campaign.name} » ? Son adresse répondra 404.`, {danger: true}))) return;
        const result = await apiAdminDeleteCampaign(campaign.id);
        if (result?.error) { notify(result.message || result.error, "error"); return; }
        load();
    }

    async function toggleAudit() {
        if (audit) { setAudit(null); return; }
        const data = await apiAdminListCampaignAudit();
        if (!Array.isArray(data)) { notify(data?.message || data?.error || "Journal indisponible.", "error"); return; }
        setAudit(data);
    }

    return (
        <div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8}}>
                <p style={{margin: 0, fontSize: 13, color: C.textMuted, maxWidth: 520}}>
                    Pages autonomes (quiz, mini-site) publiées entre deux dates, sans mise en production.
                </p>
                <div style={{display: "flex", gap: 8}}>
                    <button onClick={toggleAudit} style={smallButton}>Journal</button>
                    <button onClick={() => setEditing(null)} style={styles.primaryButton}>+ Nouvelle campagne</button>
                </div>
            </div>

            {error && <div role="alert" style={{backgroundColor: C.dangerLight, color: C.danger, borderRadius: 8, padding: "10px 14px", marginBottom: 12}}>{error}</div>}

            {audit && (
                <div style={{...styles.card, fontSize: 13}}>
                    <strong>Journal d'audit</strong>
                    {audit.length === 0 && <div style={{color: C.textMuted}}>Aucune action enregistrée.</div>}
                    {audit.map(entry => (
                        <div key={entry.id} style={{padding: "4px 0", borderTop: `1px solid ${C.border}`}}>
                            {formatParis(entry.created_at)} · {entry.username || "compte supprimé"} · {ACTION_LABELS[entry.action] || entry.action} · {entry.campaign_slug}
                        </div>
                    ))}
                </div>
            )}

            {campaigns.length === 0 && !error && <p style={{color: C.textMuted}}>Aucune campagne pour le moment.</p>}

            {campaigns.map(campaign => (
                <div key={campaign.id} style={{border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 10}}>
                    <div style={{display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8}}>
                        <div>
                            <strong>{campaign.name}</strong>
                            <span style={{marginLeft: 8, fontSize: 12, borderRadius: 12, padding: "2px 8px", ...STATE_COLORS[campaign.state]}}>{STATE_LABELS[campaign.state]}</span>
                            {campaign.is_main && <span style={{marginLeft: 6, fontSize: 12, color: C.primary}}>★ Principale</span>}
                            {campaign.isolated === false && (
                                <span
                                    title="Le code de cette page pourrait lire la session des personnes connectées qui l'ouvrent."
                                    style={{marginLeft: 6, fontSize: 12, borderRadius: 12, padding: "2px 8px", backgroundColor: C.dangerLight, color: C.danger}}
                                >Non isolée</span>
                            )}
                            <div style={{fontSize: 12, color: C.textMuted, marginTop: 4}}>
                                {campaign.url} · du {formatParis(campaign.starts_at)} au {formatParis(campaign.ends_at)} (heure de Paris)
                            </div>
                        </div>
                        <div style={{display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start"}}>
                            <button onClick={() => openPreview(campaign)} style={smallButton}>Aperçu</button>
                            <button onClick={() => copyLink(campaign)} style={smallButton}>Copier le lien</button>
                            <button onClick={() => setEditing(campaign)} style={smallButton}>Modifier</button>
                            <button onClick={() => toggleActive(campaign)} style={smallButton}>{campaign.active ? "Désactiver" : "Activer"}</button>
                            <button onClick={() => remove(campaign)} style={{...smallButton, color: C.danger}}>Supprimer</button>
                        </div>
                    </div>
                </div>
            ))}

            {editing !== undefined && (
                <CampaignFormModal
                    campaign={editing}
                    onClose={() => setEditing(undefined)}
                    onSaved={() => { setEditing(undefined); load(); }}
                />
            )}
        </div>
    );
}
